
import React from "react";

import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";

import {
  useGetInvoicesQuery,
  useMarkInvoicePaidMutation,
  useCancelInvoiceMutation,
} from "../../slices/billingApiSlice";

const InvoiceListScreen = () => {
  const navigate = useNavigate();

  // =====================================================
  // RTK QUERY
  // =====================================================

  const { data, isLoading, isFetching, refetch } = useGetInvoicesQuery({
    page: 1,
    limit: 20,
  });

  const [markInvoicePaid, { isLoading: paying }] = useMarkInvoicePaidMutation();

  const [cancelInvoice, { isLoading: cancelling }] = useCancelInvoiceMutation();

  const invoices = data?.data || [];

  // =====================================================
  // Helpers
  // =====================================================

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatStatus = (status) => {
    if (!status) return "-";

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "-";

    return method
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // =====================================================
  // Discount Helpers
  // =====================================================

  const getDiscountLabel = (invoice) => {
    const discount = invoice?.discount;

    if (!discount?.enabled) {
      return "None";
    }

    if (!discount?.applied) {
      return "Not applied";
    }

    if (discount.type === "percentage") {
      return `${discount.value || 0}%`;
    }

    if (discount.type === "fixed") {
      return formatCurrency(discount.value || 0);
    }

    return "Applied";
  };

  const getDiscountAmount = (invoice) => {
    return Number(invoice?.discountAmount ?? invoice?.discount?.amount ?? 0);
  };

  // =====================================================
  // Mark Paid
  // =====================================================

  const markPaidHandler = async (invoiceId) => {
    const confirmPayment = window.confirm(
      "Are you sure you want to mark this invoice as paid?",
    );

    if (!confirmPayment) return;

    try {
      await markInvoicePaid({
        invoiceId,
        data: {},
      }).unwrap();

      toast.success("Invoice marked as paid");

      refetch();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.error || "Unable to update invoice",
      );
    }
  };

  // =====================================================
  // Cancel Invoice
  // =====================================================

  const cancelHandler = async (invoiceId) => {
    const confirmCancel = window.confirm(
      "Are you sure you want to cancel this invoice?",
    );

    if (!confirmCancel) return;

    try {
      await cancelInvoice({
        invoiceId,
        reason: "Cancelled by admin",
      }).unwrap();

      toast.success("Invoice cancelled");

      refetch();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.error || "Unable to cancel invoice",
      );
    }
  };

  // =====================================================
  // Status Style
  // =====================================================

  const statusStyle = (status) => {
    switch (status) {
      case "paid":
        return `
          bg-green-100
          text-green-700
        `;

      case "pending":
        return `
          bg-yellow-100
          text-yellow-700
        `;

      case "overdue":
        return `
          bg-orange-100
          text-orange-700
        `;

      case "cancelled":
        return `
          bg-red-100
          text-red-700
        `;

      default:
        return `
          bg-gray-100
          text-gray-700
        `;
    }
  };

  // =====================================================
  // Render
  // =====================================================

  return (
    <div>
      {/* =================================================
          HEADER
      ================================================= */}

      <div
        className="
          flex
          flex-col
          md:flex-row
          justify-between
          items-start
          md:items-center
          gap-4
          mb-6
        "
      >
        <div>
          <h1
            className="
              text-2xl
              font-bold
              text-gray-800
            "
          >
            Billing Invoices
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Manage generated invoices, discounts, payments, and billing status
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/billing/invoices/generate")}
          className="
            bg-blue-600
            hover:bg-blue-700
            text-white
            px-5
            py-2.5
            rounded-lg
            font-medium
          "
        >
          Generate Invoice
        </button>
      </div>

      {/* =================================================
          LOADING
      ================================================= */}

      {isLoading ? (
        <div
          className="
            flex
            justify-center
            py-16
          "
        >
          <div
            className="
              animate-spin
              h-10
              w-10
              border-4
              border-blue-600
              border-t-transparent
              rounded-full
            "
          />
        </div>
      ) : invoices.length === 0 ? (
        /* =================================================
            EMPTY STATE
        ================================================= */

        <div
          className="
            bg-white
            rounded-xl
            shadow-sm
            border
            border-gray-200
            p-10
            text-center
          "
        >
          <h2
            className="
              text-lg
              font-semibold
              text-gray-800
            "
          >
            No invoices found
          </h2>

          <p className="text-gray-500 mt-2">
            Generate an invoice to see it here.
          </p>

          <button
            type="button"
            onClick={() => navigate("/billing/invoices/generate")}
            className="
              mt-5
              bg-blue-600
              hover:bg-blue-700
              text-white
              px-5
              py-2
              rounded-lg
            "
          >
            Generate Invoice
          </button>
        </div>
      ) : (
        /* =================================================
            TABLE
        ================================================= */

        <div
          className="
            bg-white
            rounded-xl
            shadow-sm
            border
            border-gray-200
            overflow-hidden
          "
        >
          {/* Refresh indicator */}

          {isFetching && !isLoading && (
            <div
              className="
                px-4
                py-2
                bg-blue-50
                text-blue-600
                text-xs
                text-center
              "
            >
              Updating invoices...
            </div>
          )}

          <div className="overflow-x-auto">
            <table
              className="
                min-w-full
                text-sm
              "
            >
              {/* =================================================
                  TABLE HEADER
              ================================================= */}

              <thead
                className="
                  bg-gray-50
                  border-b
                "
              >
                <tr>
                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Invoice No
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Coaching Center
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Plan
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Invoice Date
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Billing Period
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-right
                      font-semibold
                      text-gray-700
                    "
                  >
                    Subtotal
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-right
                      font-semibold
                      text-gray-700
                    "
                  >
                    Discount
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-right
                      font-semibold
                      text-gray-700
                    "
                  >
                    Taxable
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-right
                      font-semibold
                      text-gray-700
                    "
                  >
                    GST
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-right
                      font-semibold
                      text-gray-700
                    "
                  >
                    Total
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Status
                  </th>

                  <th
                    className="
                      px-4
                      py-3
                      text-left
                      font-semibold
                      text-gray-700
                    "
                  >
                    Actions
                  </th>
                </tr>
              </thead>

              {/* =================================================
                  TABLE BODY
              ================================================= */}

              <tbody>
                {invoices.map((invoice) => {
                  const discountAmount = getDiscountAmount(invoice);

                  const minimumInvoiceApplied =
                    invoice.minimumInvoiceApplied === true;

                  return (
                    <tr
                      key={invoice._id}
                      className="
                        hover:bg-gray-50
                        border-b
                        last:border-b-0
                      "
                    >
                      {/* Invoice Number */}

                      <td className="px-4 py-4">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/billing/invoices/${invoice._id}`)
                          }
                          className="
                            font-semibold
                            text-blue-600
                            hover:text-blue-800
                            hover:underline
                          "
                        >
                          {invoice.invoiceNumber}
                        </button>
                      </td>

                      {/* Coaching Center */}

                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-800">
                          {invoice.coachingCenter?.name ||
                            invoice.coachingCenterName ||
                            "-"}
                        </div>

                        {invoice.phone && (
                          <div className="text-xs text-gray-500 mt-1">
                            {invoice.phone}
                          </div>
                        )}
                      </td>

                      {/* Plan */}

                      <td className="px-4 py-4">
                        {invoice.billingPlan ||
                          invoice.subscription?.plan ||
                          invoice.subscription?.name ||
                          "-"}
                      </td>

                      {/* Invoice Date */}

                      <td className="px-4 py-4 whitespace-nowrap">
                        {formatDate(invoice.invoiceDate)}
                      </td>

                      {/* Billing Period */}

                      <td className="px-4 py-4 whitespace-nowrap">
                        {formatDate(invoice.billingPeriodStart)}

                        <span className="text-gray-400 mx-1">-</span>

                        {formatDate(invoice.billingPeriodEnd)}
                      </td>

                      {/* Calculated Subtotal */}

                      <td
                        className="
                          px-4
                          py-4
                          text-right
                          whitespace-nowrap
                        "
                      >
                        {formatCurrency(
                          invoice.calculatedSubtotal ?? invoice.subtotal,
                        )}
                      </td>

                      {/* Discount */}

                      <td
                        className="
                          px-4
                          py-4
                          text-right
                          whitespace-nowrap
                        "
                      >
                        {discountAmount > 0 ? (
                          <>
                            <div className="font-medium text-green-600">
                              - {formatCurrency(discountAmount)}
                            </div>

                            {invoice.discount?.applied && (
                              <div
                                className="
                                  text-xs
                                  text-gray-500
                                  mt-1
                                "
                              >
                                {getDiscountLabel(invoice)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* Taxable Amount */}

                      <td
                        className="
                          px-4
                          py-4
                          text-right
                          whitespace-nowrap
                        "
                      >
                        <div className="font-medium text-gray-800">
                          {formatCurrency(invoice.taxableAmount)}
                        </div>

                        {minimumInvoiceApplied && (
                          <div
                            className="
                              text-xs
                              text-yellow-700
                              mt-1
                            "
                          >
                            Minimum applied
                          </div>
                        )}
                      </td>

                      {/* GST */}

                      <td
                        className="
                          px-4
                          py-4
                          text-right
                          whitespace-nowrap
                        "
                      >
                        <div className="font-medium">
                          {formatCurrency(invoice.totalTaxAmount)}
                        </div>

                        <div
                          className="
                            text-xs
                            text-gray-500
                            mt-1
                          "
                        >
                          {invoice.gstRate || 0}%
                        </div>

                        {invoice.gstType && invoice.gstType !== "none" && (
                          <div
                            className="
                                text-xs
                                text-gray-400
                                mt-0.5
                              "
                          >
                            {invoice.gstType === "cgst_sgst"
                              ? "CGST + SGST"
                              : "IGST"}
                          </div>
                        )}
                      </td>

                      {/* Total */}

                      <td
                        className="
                          px-4
                          py-4
                          text-right
                          whitespace-nowrap
                        "
                      >
                        <strong className="text-gray-900">
                          {formatCurrency(invoice.total)}
                        </strong>

                        {Number(invoice.paidAmount || 0) > 0 && (
                          <div
                            className="
                              text-xs
                              text-green-600
                              mt-1
                            "
                          >
                            Paid {formatCurrency(invoice.paidAmount)}
                          </div>
                        )}

                        {Number(invoice.paidAmount || 0) <
                          Number(invoice.total || 0) &&
                          invoice.status !== "cancelled" &&
                          Number(invoice.paidAmount || 0) > 0 && (
                            <div
                              className="
                                text-xs
                                text-orange-600
                                mt-1
                              "
                            >
                              Balance{" "}
                              {formatCurrency(
                                Math.max(
                                  Number(invoice.total || 0) -
                                    Number(invoice.paidAmount || 0),
                                  0,
                                ),
                              )}
                            </div>
                          )}
                      </td>

                      {/* Status */}

                      <td className="px-4 py-4">
                        <span
                          className={`
                            inline-flex
                            px-3
                            py-1
                            rounded-full
                            text-xs
                            font-semibold
                            ${statusStyle(invoice.status)}
                          `}
                        >
                          {formatStatus(invoice.status)}
                        </span>
                      </td>

                      {/* Actions */}

                      <td className="px-4 py-4">
                        <div
                          className="
                            flex
                            gap-2
                            flex-wrap
                            min-w-[220px]
                          "
                        >
                          {/* View */}

                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/billing/invoices/${invoice._id}`)
                            }
                            className="
                              bg-cyan-600
                              hover:bg-cyan-700
                              text-white
                              px-3
                              py-1.5
                              rounded
                              text-xs
                            "
                          >
                            View
                          </button>

                          {/* Regenerate */}

                          {invoice.status === "pending" && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/billing/invoices/${invoice._id}/regenerate`,
                                )
                              }
                              className="
                                bg-gray-600
                                hover:bg-gray-700
                                text-white
                                px-3
                                py-1.5
                                rounded
                                text-xs
                              "
                            >
                              Regenerate
                            </button>
                          )}

                          {/* Mark Paid */}

                          {invoice.status === "pending" && (
                            <button
                              type="button"
                              disabled={paying}
                              onClick={() => markPaidHandler(invoice._id)}
                              className="
                                bg-green-600
                                hover:bg-green-700
                                text-white
                                px-3
                                py-1.5
                                rounded
                                text-xs
                                disabled:opacity-50
                                disabled:cursor-not-allowed
                              "
                            >
                              {paying ? "Processing..." : "Mark Paid"}
                            </button>
                          )}

                          {/* Cancel */}

                          {invoice.status === "pending" && (
                            <button
                              type="button"
                              disabled={cancelling}
                              onClick={() => cancelHandler(invoice._id)}
                              className="
                                bg-red-600
                                hover:bg-red-700
                                text-white
                                px-3
                                py-1.5
                                rounded
                                text-xs
                                disabled:opacity-50
                                disabled:cursor-not-allowed
                              "
                            >
                              {cancelling ? "Cancelling..." : "Cancel"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceListScreen;
